import { z } from 'zod';
/**
 * Служебный канал между `api` и `access-gateway`.
 *
 * Туннели к агентам держит `api` — это его роль по §3.2, и держать их
 * в двух местах нельзя: агент подключается один раз, одним соединением.
 * Значит, шлюзу нужен способ попросить у `api` поток до устройства.
 *
 * Канал служебный во всех смыслах: обратный прокси его наружу не пускает,
 * а `api` принимает только предъявителя общего секрета. Подробности
 * и разбор альтернатив — ADR 013.
 */
export const INTERNAL_STREAM_PATH = '/api/v1/internal/stream';
/** Заголовок с общим секретом. Не `Authorization`: это не пользователь. */
export const INTERNAL_TOKEN_HEADER = 'x-netdeck-internal';
/**
 * Параметры запрашиваемого потока — в строке запроса, потому что открытие
 * WebSocket тела не имеет.
 */
export const internalStreamQuerySchema = z.object({
    agentId: z.uuid(),
    host: z.string().min(1).max(255),
    port: z.coerce.number().int().min(1).max(65535),
});
/**
 * Коды закрытия служебного канала.
 *
 * Из диапазона для приложений (4000–4999). Нужны, чтобы шлюз отличал
 * «агент площадки отключился» от «сломался сам канал»: причина попадает
 * в `sessions.end_reason` и в сообщение пользователю, а на «соединение
 * закрыто» смотреть бесполезно.
 */
export const InternalCloseCode = {
    /** Токен не предъявлен либо не совпал. */
    UNAUTHORIZED: 4401,
    /** Агент площадки не подключён. */
    AGENT_OFFLINE: 4404,
    /** Агент отказался открыть поток: порт закрыт, узел не отвечает. */
    STREAM_FAILED: 4502,
    /** Туннель до агента разорвался во время работы. */
    TUNNEL_LOST: 4503,
};
/**
 * Тикет на VNC-сессию: всё, что шлюзу нужно знать, чтобы её открыть.
 *
 * Лежит в Redis тридцать секунд и гасится при первом использовании (§10.1).
 * Одноразовость здесь не формальность: тикет проходит через адресную строку
 * WebSocket, а значит попадает в журналы прокси — и повторно использовать
 * его не должен ни владелец, ни тот, кто прочитал журнал.
 *
 * Маршрут (`route`) уже разрешён и лежит внутри: решение «через агента
 * или напрямую» принимает `api` (§5.1, задача 7), где есть реестр
 * подключённых агентов. Шлюз его исполняет, а не повторяет.
 *
 * Пароля в тикете нет — только идентификатор учётных данных. Расшифровка
 * происходит в шлюзе в момент подключения (§10.2), и класть расшифрованный
 * секрет в Redis означало бы обойти это требование ради удобства.
 */
export const vncTicketSchema = z.object({
    sessionId: z.uuid(),
    orgId: z.uuid(),
    userId: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    route: z.object({
        mode: z.enum(['agent', 'direct']),
        agentId: z.uuid().nullable(),
        host: z.string(),
        port: z.number().int(),
    }),
    credentialId: z.uuid().nullable(),
    viewOnly: z.boolean(),
    preferEncoding: z.enum(['tight', 'zrle', 'hextile']),
    clientIp: z.string().nullable(),
});
/** Ключ тикета в Redis. Префикс общий, чтобы их было видно одним `SCAN`. */
export function vncTicketKey(ticket) {
    return `vnc:ticket:${ticket}`;
}
/**
 * Тикет на SSH-сессию. Устроен как `vncTicketSchema` и по тем же причинам:
 * одноразовый, живёт тридцать секунд, маршрут внутри уже разрешён, секрета
 * в нём нет — только идентификатор учётных данных.
 *
 * Отличий два, и оба из-за протокола. Имя пользователя: у SSH оно часть
 * подключения, а не учётных данных, и на разных узлах при общем пароле
 * различается. Отпечаток ключа хоста: сверять его должен тот, кто
 * подключается, то есть шлюз, а знает закреплённое значение только `api`.
 */
export const sshTicketSchema = z.object({
    sessionId: z.uuid(),
    orgId: z.uuid(),
    userId: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    route: z.object({
        mode: z.enum(['agent', 'direct']),
        agentId: z.uuid().nullable(),
        host: z.string(),
        port: z.number().int(),
    }),
    credentialId: z.uuid().nullable(),
    username: z.string().min(1).max(100),
    authMethod: z.enum(['password', 'key', 'keyboard_interactive']),
    allowLegacyAlgorithms: z.boolean(),
    term: z.string().min(1).max(64),
    /**
     * Закреплённый отпечаток. NULL — узла ещё не касались: шлюз запомнит
     * ключ и вернёт его `api` (TOFU, ADR 026). Непустое значение шлюз обязан
     * сверить и при расхождении не аутентифицироваться вовсе.
     */
    hostKeyFp: z.string().nullable(),
    clientIp: z.string().nullable(),
});
/** Ключ тикета в Redis. Префикс свой, чтобы не путать с тикетами VNC. */
export function sshTicketKey(ticket) {
    return `ssh:ticket:${ticket}`;
}
/** Тело служебного запроса на зондирование SSH: `POST /internal/ssh/probe`. */
export const internalSshProbeRequestSchema = z.object({
    mode: z.enum(['agent', 'direct']),
    agentId: z.uuid().nullable(),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    /** Под каким именем спрашивать способы аутентификации. */
    username: z.string().min(1).max(100),
    allowLegacyAlgorithms: z.boolean().default(false),
    /**
     * Закреплённый отпечаток ключа хоста, если он есть.
     *
     * Нужен, чтобы зондирование могло ответить «ключ сменился»: сверять его
     * умеет только тот, у кого он записан, а записан он в базе у `api`.
     * Закреплять новый зондирование при этом не вправе (ADR 026).
     */
    expectedHostKeyFp: z.string().max(200).nullable().default(null),
    timeoutMs: z.number().int().min(500).max(30_000).default(5_000),
});
/** Тело служебного запроса на зондирование: `POST /internal/probe` у шлюза. */
export const internalProbeRequestSchema = z.object({
    mode: z.enum(['agent', 'direct']),
    agentId: z.uuid().nullable(),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    timeoutMs: z.number().int().min(500).max(30_000).default(5_000),
});
// — Файловый обмен: служебные запросы к шлюзу (§11 фаза 6) ————————
/**
 * Куда шлюзу идти за файлами.
 *
 * Тот же набор, что и в тикете сессии, и по той же причине: маршрут выбирает
 * `api` (§5.1, задача 7) — только у него есть реестр подключённых агентов, —
 * а шлюз его исполняет. Пароля здесь нет, только идентификатор учётных
 * данных: расшифровка происходит в шлюзе в момент подключения (§10.2).
 *
 * `sessionId` — не формальность. По нему шлюз, во-первых, находит уже
 * поднятый файловый канал, а во-вторых, проверяет в базе, что сессия жива:
 * иначе файлы остались бы доступны по идентификатору закрытой сессии.
 */
export const internalFsTargetSchema = z.object({
    sessionId: z.uuid(),
    /**
     * Каким протоколом идти за файлами (фаза 9b).
     *
     * Единственное, что `api` сообщает шлюзу о протоколе, и берётся оно
     * из вида сессии, а не из отдельного решения: у сессии `vnc` файлы идут
     * расширением TightVNC, у `ssh` — по SFTP. Всё остальное в этом запросе
     * для обоих одинаково, потому и разошлись они одним полем, а не двумя
     * наборами маршрутов.
     */
    protocol: z.enum(['tightvnc', 'sftp']).default('tightvnc'),
    route: z.object({
        mode: z.enum(['agent', 'direct']),
        agentId: z.uuid().nullable(),
        host: z.string(),
        port: z.number().int(),
    }),
    credentialId: z.uuid().nullable(),
    /**
     * Что нужно SFTP сверх маршрута и учётных данных: под кем входить, чем
     * аутентифицироваться и какой ключ хоста считать своим.
     *
     * У сессии `vnc` поля нет — там за подключение отвечает пароль и больше
     * ничего. Складывать эти поля в корень запроса значило бы приучать
     * файловый протокол TightVNC к параметрам, которых он не знает.
     */
    ssh: z
        .object({
        username: z.string().min(1).max(100),
        authMethod: z.enum(['password', 'key', 'keyboard_interactive']),
        allowLegacyAlgorithms: z.boolean(),
        hostKeyFp: z.string().nullable(),
    })
        .nullish(),
});
export const internalFsPathRequestSchema = internalFsTargetSchema.extend({
    path: z.string().max(4096),
});
export const internalFsRenameRequestSchema = internalFsTargetSchema.extend({
    from: z.string().min(1).max(4096),
    to: z.string().min(1).max(4096),
});
export const internalFsTransferRequestSchema = internalFsTargetSchema.extend({
    path: z.string().min(1).max(4096),
    /** С какого байта продолжать (§11 фаза 6, задача 6). */
    offset: z.number().int().nonnegative().default(0),
});
/**
 * Заголовок, в котором параметры выгрузки едут к шлюзу.
 *
 * Тело запроса на выгрузку занято самим файлом, а строка запроса — плохое
 * место для путей с кириллицей: они пройдут через журналы прокси в виде
 * процентных последовательностей, и разбирать их там станет некому.
 * Значение — base64url от JSON по `internalFsTransferRequestSchema`.
 */
export const INTERNAL_FS_PARAMS_HEADER = 'x-netdeck-fs';
/** Служебный маршрут зондирования SSH у шлюза. */
export const INTERNAL_SSH_PROBE_PATH = '/internal/ssh/probe';
/** Пути служебных файловых маршрутов шлюза. Наружу прокси их не пускает. */
export const INTERNAL_FS_PATHS = {
    LIST: '/internal/fs/list',
    STAT: '/internal/fs/stat',
    MKDIR: '/internal/fs/mkdir',
    REMOVE: '/internal/fs/remove',
    RENAME: '/internal/fs/rename',
    DOWNLOAD: '/internal/fs/download',
    UPLOAD: '/internal/fs/upload',
};
//# sourceMappingURL=internal.js.map